import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faFilter, faCalendarAlt, faChevronLeft, faChevronRight, faEye, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import { Spinner, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation, useApolloClient } from "@apollo/client";

// GraphQL Queries
import { SELLER_PRODUCTS_QUERY } from "api/graphql/queries/product/GetInvProducts";
import { GET_INVENTORY_BY_SELLER } from "api/graphql/queries/inventory/getInventoryBySeller";
import { CANCEL_INVENTORY_REQUEST } from "api/graphql/mutations/inventory/cancelInventoryRequest";

// Components
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import Badge from "components/base/Badge";
import Button from "components/base/Button";
import InventoryHistoryFilterGroup, { FilterMenu } from "components/modules/e-commerce/admin/InventoryHistoryFilterGroup";
import EditInventoryRequestModal from "components/modules/e-commerce/products/EditInventoryRequestModal";

// Hooks and Services
import useAdvanceTable from "hooks/useAdvanceTable";
import { ColumnDef, PaginationState } from "@tanstack/react-table";

// Types
// Use types directly from inventoryService for consistency
import { InventoryItem, InventoryRequest, InventoryResponse } from "services/inventoryService";

// Interface for product details from the GraphQL API
interface ProductDetails {
  entityid: string;
  name: string;
  sku: string;
  image1: {
    url: string;
    label: string;
  };
  stock_data: {
    qty: number;
    is_in_stock: boolean;
  };
}

// Extended type to include the additional fields from the API response
interface ExtendedInventoryRequest extends InventoryRequest {
  "date Demandé"?: string;
  "date prévue"?: string;
  scheduledDate?: string;
  "Move Type"?: string;
  "Stock Type"?: boolean | string;
  // Remove references to nombre_piece and replace with reference_number if needed
  reference_number?: string;
  // Add create_date property
  create_date?: string;
}

const InventoryHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State for inventory requests
  const [inventoryRequests, setInventoryRequests] = useState<ExtendedInventoryRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sellerId, setSellerId] = useState<string>("");
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  
  // Filter states
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [activeStatusLabel, setActiveStatusLabel] = useState<string>("Statut");
  const [stockTypeFilter, setStockTypeFilter] = useState<string>("");
  const [activeStockTypeLabel, setActiveStockTypeLabel] = useState<string>("Type de Stock");
  
  // Modal state
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingRequest, setEditingRequest] = useState<ExtendedInventoryRequest | null>(null);
  
  // Fetch Seller ID from localStorage
  useEffect(() => {
    const storedSeller = localStorage.getItem("seller");
    if (storedSeller) {
      try {
        const parsedSeller = JSON.parse(storedSeller);
        if (parsedSeller?.seller_id) {
          setSellerId(parsedSeller.seller_id.toString());
        }
      } catch (error) {
        console.error("Error parsing seller data:", error);
        toast.error("Failed to load seller information");
      }
    }
  }, []);
  
  
  // Format date for API
  const formatDateForApi = (date: Date): string => {
    const pad = (num: number) => num.toString().padStart(2, "0");
    
    // API expects DD-MM-YYYY format as per error: "time data does not match format '%d-%m-%Y %H:%M:%S'"
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  };
  
  // GraphQL queries and mutations
  const [getInventory, { loading: inventoryLoading }] = useLazyQuery(GET_INVENTORY_BY_SELLER, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getInventoryBySeller) {
        const result = data.getInventoryBySeller;
        
        if (result.status === "success") {
          // Process inventory requests
          const inventoryRequests = result.inventory_requests || [];
          
          // Ensure each request has the required properties
          const validRequests = inventoryRequests.map((req: any) => {
            return {
              ...req,
              status: req.status || 'unknown',
              inventory_list: req.inventory_list || [],
              date: req.date_demande || req.date || req.create_date || '',
              scheduledDate: req.date_prevue || req.scheduled_date || '',
              "date Demandé": req.date_demande || req.date || req.create_date || '',
              "date prévue": req.date_prevue || req.scheduled_date || '',
              "Move Type": req.move_type_odoo || req.move_type || 'in',
              "Stock Type": req.stock_type_odoo || req.stock_type || 'FBW'
            };
          });
          
          // Log the processed data
          console.log('Processed inventory data:', validRequests);
          
          setInventoryRequests(validRequests);
          
          // Get total count from pagination
          if (result.pagination) {
            setTotalCount(result.pagination.total_records);
            
            // Update pagination state based on API response
            setPagination(prev => ({
              ...prev,
              pageIndex: (result.pagination?.page || 1) - 1,
              pageSize: result.pagination?.page_size || 10
            }));
          } else {
            setTotalCount(validRequests.length);
          }
        } else {
          console.error("Error in GraphQL response:", result.message);
          toast.error("Failed to fetch inventory requests: " + result.message);
          setInventoryRequests([]);
          setTotalCount(0);
        }
      }
      setLoading(false);
    },
    onError: (error) => {
      console.error("GraphQL error:", error);
      toast.error("Failed to fetch inventory requests: " + error.message);
      setInventoryRequests([]);
      setTotalCount(0);
      setLoading(false);
    }
  });
  
  const [cancelRequest, { loading: cancelLoading }] = useMutation(CANCEL_INVENTORY_REQUEST);
  
  // Fetch Inventory Requests
  const fetchInventoryRequests = async () => {
    if (!sellerId) return;
    
    setLoading(true);
    
    try {
      const fromDateStr = fromDate ? formatDateForApi(fromDate) : "";
      const toDateStr = toDate ? formatDateForApi(toDate) : "";
      
      // Execute GraphQL query
      await getInventory({
        variables: {
          seller_id: sellerId,
          from_date: fromDateStr,
          to_date: toDateStr,
          status: statusFilter,
          page: pagination.pageIndex + 1,
          page_size: pagination.pageSize,
          stock_type: stockTypeFilter
        }
      });
      
    } catch (error: any) {
      console.error("Error fetching inventory requests:", error);
      toast.error("Failed to fetch inventory requests: " + error.message);
      setInventoryRequests([]);
      setTotalCount(0);
      setLoading(false);
    }
  };
  
  // Initial fetch and refetch on filter/pagination changes
  useEffect(() => {
    if (sellerId) {
      fetchInventoryRequests();
    }
  }, [sellerId, pagination.pageIndex, pagination.pageSize, statusFilter, stockTypeFilter, fromDate, toDate]);
  
  // Handle page change
  const handlePageChange = (newPageIndex: number) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPageIndex,
    }));
  };
  
  // Status filter handler
  const setStatusActiveFilter = (status: string, label: string) => {
    setStatusFilter(status);
    // Use a direct string rather than an object
    setActiveStatusLabel(status === "" ? "Statut" : label);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };
  
  // Stock type filter handler
  const setStockTypeActiveFilter = (stockType: string, label: string) => {
    setStockTypeFilter(stockType);
    // Use a direct string rather than an object
    setActiveStockTypeLabel(stockType === "" ? "Type de Stock" : label);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };
  
  // Filter menus
  const filterMenus: FilterMenu[] = [
    {
      label: activeStatusLabel,
      items: [
        { label: t("inventoryHistory.status.draft"), onClick: () => setStatusActiveFilter("draft", t("inventoryHistory.status.draft")) },
        { label: t("inventoryHistory.status.assigned"), onClick: () => setStatusActiveFilter("assigned", t("inventoryHistory.status.assigned")) },
        { label: t("inventoryHistory.status.done"), onClick: () => setStatusActiveFilter("done", t("inventoryHistory.status.done")) },
        { label: t("inventoryHistory.status.cancelled"), onClick: () => setStatusActiveFilter("cancel", t("inventoryHistory.status.cancelled")) },
        { label: t("all"), onClick: () => setStatusActiveFilter("", "Statut") },
      ]
    },
    {
      label: activeStockTypeLabel,
      items: [
        { label: t("inventoryHistory.stockType.fbw"), onClick: () => setStockTypeActiveFilter("FBW", t("inventoryHistory.stockType.fbw")) },
        { label: t("inventoryHistory.stockType.easyShipping"), onClick: () => setStockTypeActiveFilter("drop", t("inventoryHistory.stockType.easyShipping")) },
        { label: t("all"), onClick: () => setStockTypeActiveFilter("", "Type de Stock") },
      ]
    },
  ];
  
  // Handle cancel inventory request using GraphQL mutation
  const handleCancelRequest = async (invId: string) => {
    if (!window.confirm(t("inventoryHistory.confirmCancel"))) {
      return;
    }
    
    try {
      const { data } = await cancelRequest({
        variables: {
          request_id: invId
        }
      });
      
      if (data?.cancelInventoryRequest?.status === "success") {
        toast.success(data.cancelInventoryRequest.message || t("inventoryHistory.cancelSuccess"));
        fetchInventoryRequests(); // Refresh the list
      } else {
        toast.error(data?.cancelInventoryRequest?.message || t("inventoryHistory.cancelError"));
      }
    } catch (error: any) {
      console.error("Error cancelling inventory request:", error);
      toast.error(t("inventoryHistory.cancelError") + ": " + error.message);
    }
  };
  
  // Handler for edit button
  const handleEditRequest = (request: ExtendedInventoryRequest) => {
    setEditingRequest(request);
    setShowEditModal(true);
  };
  
  // Handler for inventory ID click
  const handleInventoryIdClick = (inventoryId: string) => {
    navigate(`/inventory/details/${inventoryId}`);
  };
  
  // Get status color based on status name
  const getStatusColor = (status: string): string => {
    switch (status) {
      case "primary": return "#4e73df";
      case "secondary": return "#858796";
      case "success": return "#1cc88a";
      case "info": return "#36b9cc";
      case "warning": return "#f6c23e";
      case "danger": return "#e74a3b";
      default: return "#858796";
    }
  };
  
  // Get move type badge style
  const getMoveTypeInfo = (moveType: string): { color: string; icon: any; label: string } => {
    const type = moveType?.toLowerCase() || 'unknown';
    switch (type) {
      case "in":
        return { 
          color: "success", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-down ms-1" style={{ height: '12.8px', width: '12.8px' }}><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>,
          label: "Stock In" 
        };
      case "out":
        return { 
          color: "danger", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-up ms-1" style={{ height: '12.8px', width: '12.8px' }}><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>,
          label: "Stock Out" 
        };
      default:
        return { 
          color: "info", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-shuffle ms-1" style={{ height: '12.8px', width: '12.8px' }}><polyline points="16 3 21 3 21 8"></polyline><line x1="4" y1="20" x2="21" y2="3"></line><polyline points="21 16 21 21 16 21"></polyline><line x1="15" y1="15" x2="21" y2="21"></line><line x1="4" y1="4" x2="9" y2="9"></line></svg>,
          label: moveType || "Unknown" 
        };
    }
  };

  // Get status badge color and icon
  const getStatusInfo = (status: string): { color: string; icon: any; label: string } => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "draft":
        return { 
          color: "secondary", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-file ms-1" style={{ height: '12.8px', width: '12.8px' }}><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>,
          label: t("inventoryHistory.status.draft")
        };
      case "ready":
        return { 
          color: "info", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-info ms-1" style={{ height: '12.8px', width: '12.8px' }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>,
          label: t("inventoryHistory.status.ready")
        };
      case "done":
        return { 
          color: "success", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-check ms-1" style={{ height: '12.8px', width: '12.8px' }}><polyline points="20 6 9 17 4 12"></polyline></svg>,
          label: t("inventoryHistory.status.done")
        };
      case "assigned":
        return { 
          color: "primary", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-package ms-1" style={{ height: '12.8px', width: '12.8px' }}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>,
          label: t("inventoryHistory.status.assigned")
        };
      case "error":
        return { 
          color: "warning", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-alert-triangle ms-1" style={{ height: '12.8px', width: '12.8px' }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>,
          label: t("inventoryHistory.status.waiting")
        };
      case "cancel":
        return { 
          color: "danger", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-x ms-1" style={{ height: '12.8px', width: '12.8px' }}><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
          label: t("inventoryHistory.status.cancelled")
        };
      default:
        return { 
          color: "warning", 
          icon: <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-alert-octagon ms-1" style={{ height: '12.8px', width: '12.8px' }}><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
          label: t("inventoryHistory.status.unknown")
        };
    }
  };
  
  // Format date nicely
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    
    try {
      const date = new Date(dateStr);
      
      // Format date: Mar 27, 2025 at 12:30 PM
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(date);
    } catch (e) {
      return dateStr;
    }
  };
  
  // Table columns definition
  const columns: ColumnDef<ExtendedInventoryRequest>[] = [
    {
      id: "inv_id",
      accessorKey: "inv_id",
      header: t("inventoryHistory.tableHeaders.inventoryId"),
      cell: ({ row }) => (
        <div
          className="fw-semibold text-primary cursor-pointer"
          onClick={() => handleInventoryIdClick(row.original.inv_id)}
          style={{ cursor: "pointer" }}
        >
          <span style={{ textDecoration: "underline" }}>{row.original.inv_id}</span>
        </div>
      ),
      meta: {
        headerProps: { style: { width: "15%" }, className: "ps-3" },
        cellProps: { className: "ps-3" },
      },
    },
    {
      id: "status",
      accessorKey: "status",
      header: t("inventoryHistory.tableHeaders.status"),
      cell: ({ row }) => {
        const status = row.original.status || 'unknown';
        const { color, label, icon } = getStatusInfo(status);
        
        return (
          <span className={`badge border border-${color} text-${color} bg-transparent fs-10 py-1 px-2 rounded-2`}>
            {icon}
            <span className="ms-1">{label}</span>
          </span>
        );
      },
      meta: {
        headerProps: { style: { width: "12%" } },
      },
    },
    {
      id: "moveType",
      accessorKey: "Move Type",
      header: t("inventoryHistory.tableHeaders.moveType"),
      cell: ({ row }) => {
        // Use type assertion to access the special field name
        const extendedRow = row.original as any;
        const moveType = extendedRow["Move Type"] || 'unknown';
        const moveTypeInfo = getMoveTypeInfo(moveType);
        const color = moveTypeInfo.color;
        const icon = moveTypeInfo.icon;
        // Use translation for the label based on move type
        const label = moveType?.toLowerCase() === 'in' 
          ? t("inventoryHistory.moveType.in") 
          : moveType?.toLowerCase() === 'out'
          ? t("inventoryHistory.moveType.out")
          : t("inventoryHistory.moveType.unknown");
        
        return (
          <span className={`badge border border-${color} text-${color} bg-transparent fs-10 py-1 px-2 rounded-2`}>
            {icon}
            <span className="ms-1">{label}</span>
          </span>
        );
      },
      meta: {
        headerProps: { style: { width: "12%" } },
      },
    },
    {
      id: "create_date",
      accessorKey: "create_date",
      header: t("inventoryHistory.tableHeaders.creationDate"),
      cell: ({ row }) => {
        const dateStr = row.original.create_date || '';
        
        return (
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
            <span>{formatDate(dateStr)}</span>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: "20%" } },
      },
    },
    {
      id: "date",
      accessorKey: "date",
      header: t("inventoryHistory.tableHeaders.requestedDate"),
      cell: ({ row }) => {
        // Use type assertion to access the special field names
        const extendedRow = row.original as any;
        
        // Check if Stock Type is "drop"
        const stockType = extendedRow["Stock Type"];
        if (stockType === "drop") {
          return (
            <div className="d-flex align-items-center">
              <span className="text-muted">-</span>
            </div>
          );
        }
        
        // Use the date Demandé field
        const dateStr = extendedRow["date Demandé"] || row.original.date;
        
        return (
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
            <span>{formatDate(dateStr)}</span>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: "25%" } },
      },
    },
    {
      id: "scheduledDate",
      accessorKey: "scheduledDate",
      header: t("inventoryHistory.tableHeaders.scheduledDate"),
      cell: ({ row }) => {
        // Use type assertion to access the special field names
        const extendedRow = row.original as any;
        
        // Check if Stock Type is "drop"
        const stockType = extendedRow["Stock Type"];
        if (stockType === "drop") {
          return (
            <div className="d-flex align-items-center">
              <span className="text-muted">-</span>
            </div>
          );
        }
        
        // Use the date prévue field
        const dateStr = extendedRow["date prévue"] || row.original.scheduledDate;
        
        return (
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
            <span>{formatDate(dateStr)}</span>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: "25%" } },
      },
    },
    {
      id: "actions",
      header: t("inventoryHistory.tableHeaders.actions"),
      cell: ({ row }) => {
        const status = (row.original.status || '').toLowerCase();
        // Buttons are only active if status is draft
        const isActive = status === 'draft';
        
        return (
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant={isActive ? "outline-primary" : "outline-secondary"}
              size="sm"
              onClick={() => navigate(`/inventory/details/${row.original.inv_id}?edit=true`)}
              title={t("inventoryHistory.buttons.edit")}
              disabled={!isActive}
              className="me-1 mb-1"
            >
              <FontAwesomeIcon icon={faEdit} className="me-1" /> 
            </Button>
            <Button
              variant={isActive ? "outline-danger" : "outline-secondary"}
              size="sm"
              onClick={() => handleCancelRequest(row.original.inv_id)}
              title={t("inventoryHistory.buttons.cancel")}
              disabled={!isActive}
              className="me-1 mb-1"
            >
              <FontAwesomeIcon icon={faTrash} className="me-1" /> 
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: "20%" }, className: "text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];
  
  // Initialize table
  const table = useAdvanceTable({
    data: inventoryRequests,
    columns,
    pageSize: pagination.pageSize,
    pagination: true,
    sortable: true,
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize
      }
    }
  });
  
  // Apply date filter
  const applyDateFilter = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };
  
  return (
    <div className="p-2 p-md-4">
      {/* Header Section */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4 gap-3">
        <div>
          <h2 className="mb-1 text-body">{t("inventoryHistory.title")}</h2>
          <p className="text-body-secondary mb-0">{t("inventoryHistory.subtitle")}</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => window.location.href = "/inventory/requests"}
          className="rounded-2 px-4 w-100 w-lg-auto"
          style={{ minWidth: "fit-content" }}
        >
          {t("inventoryHistory.newRequest")}
        </Button>
      </div>
      
      {/* Filters */}
      <div className="card mb-4 border-0 shadow-sm rounded-3" style={{ overflow: 'visible' }}>
        <div className="card-header bg-body-tertiary py-3 border-bottom">
          <h5 className="mb-0 text-body">
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            {t("inventoryHistory.filterTitle")}
          </h5>
        </div>
        <div className="card-body bg-body p-3 p-md-4">
          <Row className="g-3">
            {/* First row - Stock Type and Status filters */}
            <Col xs={12} md={6} lg={3}>
              <div>
                <label className="form-label fw-semibold text-body mb-2">{t("inventoryHistory.stockTypeLabel")}</label>
                <InventoryHistoryFilterGroup menus={[filterMenus[1]]} />
              </div>
            </Col>
            
            <Col xs={12} md={6} lg={3}>
              <div>
                <label className="form-label fw-semibold text-body mb-2">{t("inventoryHistory.statusLabel")}</label>
                <InventoryHistoryFilterGroup menus={[filterMenus[0]]} />
              </div>
            </Col>
            
            {/* Second row - Date filters */}
            <Col xs={12} md={6} lg={3}>
              <div>
                <label className="form-label fw-semibold text-body mb-2">{t("inventoryHistory.fromDate")}</label>
                <div className="input-group">
                  <span className="input-group-text bg-body-tertiary border-end-0">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-body-secondary" />
                  </span>
                  <Flatpickr
                    className="form-control border-start-0 ps-1"
                    value={fromDate || undefined}
                    onChange={([date]) => setFromDate(date)}
                    options={{ 
                      dateFormat: "d/m/Y",
                      allowInput: true
                    }}
                    placeholder={t("inventoryHistory.selectFromDate")}
                  />
                </div>
              </div>
            </Col>
            
            <Col xs={12} md={6} lg={3}>
              <div>
                <label className="form-label fw-semibold text-body mb-2">{t("inventoryHistory.toDate")}</label>
                <div className="input-group">
                  <span className="input-group-text bg-body-tertiary border-end-0">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-body-secondary" />
                  </span>
                  <Flatpickr
                    className="form-control border-start-0 ps-1"
                    value={toDate || undefined}
                    onChange={([date]) => setToDate(date)}
                    options={{ 
                      dateFormat: "d/m/Y",
                      allowInput: true
                    }}
                    placeholder={t("inventoryHistory.selectToDate")}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      
      {/* Table */}
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="card-header bg-body py-3 border-bottom">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
            <h5 className="mb-0 fw-bold text-body">
              {t("inventoryHistory.inventoryRequests")}
            </h5>
            <div className="text-body-secondary small">
              {totalCount > 0 ? (
                <span>{t("inventoryHistory.showingRecords", {start: pagination.pageIndex * pagination.pageSize + 1, end: Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount), total: totalCount})}</span>
              ) : (
                <span>{t("inventoryHistory.noRequestsFound")}</span>
              )}
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <AdvanceTableProvider {...table}>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <>
                {/* Mobile card layout for small screens */}
                <div className="d-block d-lg-none">
                  {inventoryRequests.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="text-muted mb-3">
                        <FontAwesomeIcon icon={faBoxOpen} size="3x" className="opacity-50" />
                      </div>
                      <h5 className="text-muted">{t("inventoryHistory.noRequestsFound")}</h5>
                      <p className="text-body-secondary">{t("inventoryHistory.noRequestsMessage")}</p>
                    </div>
                  ) : (
                    inventoryRequests.map((request, index) => {
                      const status = request.status || 'unknown';
                      const { color: statusColor, label: statusLabel, icon: statusIcon } = getStatusInfo(status);
                      const extendedRequest = request as any;
                      const moveType = extendedRequest["Move Type"] || 'unknown';
                      const moveTypeInfo = getMoveTypeInfo(moveType);
                      const moveTypeLabel = moveType?.toLowerCase() === 'in' 
                        ? t("inventoryHistory.moveType.in") 
                        : moveType?.toLowerCase() === 'out'
                        ? t("inventoryHistory.moveType.out")
                        : t("inventoryHistory.moveType.unknown");
                      const isActive = status.toLowerCase() === 'draft';
                      
                      return (
                        <div key={request.inv_id} className="border-bottom p-3">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div
                              className="fw-semibold text-primary cursor-pointer"
                              onClick={() => handleInventoryIdClick(request.inv_id)}
                              style={{ cursor: "pointer", textDecoration: "underline" }}
                            >
                              {request.inv_id}
                            </div>
                            <div className="d-flex gap-1">
                              <Button
                                variant={isActive ? "outline-primary" : "outline-secondary"}
                                size="sm"
                                onClick={() => navigate(`/inventory/details/${request.inv_id}?edit=true`)}
                                disabled={!isActive}
                                className="p-1"
                                style={{ width: '32px', height: '32px' }}
                              >
                                <FontAwesomeIcon icon={faEdit} size="sm" />
                              </Button>
                              <Button
                                variant={isActive ? "outline-danger" : "outline-secondary"}
                                size="sm"
                                onClick={() => handleCancelRequest(request.inv_id)}
                                disabled={!isActive}
                                className="p-1"
                                style={{ width: '32px', height: '32px' }}
                              >
                                <FontAwesomeIcon icon={faTrash} size="sm" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="row g-2 mb-3">
                            <div className="col-6">
                              <div className="small text-muted mb-1">{t("inventoryHistory.tableHeaders.status")}</div>
                              <span className={`badge border border-${statusColor} text-${statusColor} bg-transparent fs-10 py-1 px-2 rounded-2`}>
                                {statusIcon}
                                <span className="ms-1">{statusLabel}</span>
                              </span>
                            </div>
                            <div className="col-6">
                              <div className="small text-muted mb-1">{t("inventoryHistory.tableHeaders.moveType")}</div>
                              <span className={`badge border border-${moveTypeInfo.color} text-${moveTypeInfo.color} bg-transparent fs-10 py-1 px-2 rounded-2`}>
                                {moveTypeInfo.icon}
                                <span className="ms-1">{moveTypeLabel}</span>
                              </span>
                            </div>
                          </div>
                          
                          <div className="small text-muted mb-1">{t("inventoryHistory.tableHeaders.creationDate")}</div>
                          <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
                            <span className="small">{formatDate(request.create_date || '')}</span>
                          </div>
                          
                          {/* Only show requested and scheduled dates if not drop stock */}
                          {extendedRequest["Stock Type"] !== "drop" && (
                            <>
                              <div className="small text-muted mb-1">{t("inventoryHistory.tableHeaders.requestedDate")}</div>
                              <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
                                <span className="small">{formatDate(extendedRequest["date Demandé"] || request.date)}</span>
                              </div>
                              
                              <div className="small text-muted mb-1">{t("inventoryHistory.tableHeaders.scheduledDate")}</div>
                              <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
                                <span className="small">{formatDate(extendedRequest["date prévue"] || request.scheduledDate)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                
                {/* Desktop table layout for large screens */}
                <div className="table-responsive d-none d-lg-block">
                  <AdvanceTable 
                    tableProps={{ 
                      className: "table align-middle table-hover mb-0",
                      style: { 
                        fontSize: '0.95rem'
                      }
                    }}
                  />
                </div>
                
                <div className="bg-body border-top py-3 px-3 px-md-4">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
                    <div className="text-body-secondary small">
                      {totalCount > 0 && (
                        <span>{t("inventoryHistory.totalRequests", {count: totalCount})}</span>
                      )}
                    </div>
                    <div>
                      {/* Custom pagination controls */}
                      <nav aria-label="Inventory pagination">
                        <ul className="pagination mb-0 flex-wrap">
                          <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => handlePageChange(pagination.pageIndex - 1)}
                              disabled={pagination.pageIndex === 0}
                              aria-label={t("inventoryHistory.buttons.previous")}
                            >
                              <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                          </li>
                          
                          {/* Generate page numbers - show fewer pages on mobile */}
                          {Array.from({ length: Math.ceil(totalCount / pagination.pageSize) })
                            .slice(
                              Math.max(0, pagination.pageIndex - 2), 
                              Math.min(Math.ceil(totalCount / pagination.pageSize), pagination.pageIndex + 3)
                            )
                            .map((_, relativeIndex) => {
                              const actualIndex = Math.max(0, pagination.pageIndex - 2) + relativeIndex;
                              return (
                                <li key={actualIndex} className={`page-item ${pagination.pageIndex === actualIndex ? 'active' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => handlePageChange(actualIndex)}
                                  >
                                    {actualIndex + 1}
                                  </button>
                                </li>
                              );
                            })}
                          
                          <li className={`page-item ${pagination.pageIndex >= Math.ceil(totalCount / pagination.pageSize) - 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => handlePageChange(pagination.pageIndex + 1)}
                              disabled={pagination.pageIndex >= Math.ceil(totalCount / pagination.pageSize) - 1}
                              aria-label={t("inventoryHistory.buttons.next")}
                            >
                              <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </AdvanceTableProvider>
        </div>
      </div>
      
      {/* Edit Inventory Request Modal */}
      <EditInventoryRequestModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        request={editingRequest}
        onRequestUpdated={fetchInventoryRequests}
      />
    </div>
  );
};

export default InventoryHistoryPage;
